import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Flex, UnstyledButton } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { useQuery } from '@tanstack/react-query'

import { GoalStatus, Order } from '@/types/actionPlan.types'

import { ActionPlanRoutePathsEnum } from '@/modules/actionPlan/routes/actionPlanRouteConfig.types'

import { ShadowBox } from '@/components/@Shared/ShadowBox/ShadowBox'
import { Button } from '@/components/Button/Button'
import { GoalsWidgetItem } from '@/components/GoalsWidgetItem/GoalsWidgetItem'
import { Loader } from '@/components/Loader/Loader'
import { Modal } from '@/components/Modal/Modal'
import { ScrollArea } from '@/components/ScrollArea/ScrollArea'
import { Text } from '@/components/Text/Text'
import { Title } from '@/components/Title/Title'

import * as Common from '@/__generated__/queries/common'
import {
  AdvisorsService,
  type GoalData,
  GoalsService,
} from '@/__generated__/requests'

import classes from './GoalsWidget.module.css'

export function GoalsWidget({ userId }: { userId?: number }) {
  const [opened, { close, open }] = useDisclosure()

  const navigate = useNavigate()

  const isAdvisorViewing = !!userId

  const queryFn = isAdvisorViewing
    ? () =>
        AdvisorsService.advisorUsersControllerGetGoals({
          userId: +userId,
        })
    : () =>
        GoalsService.goalsControllerGetAll({
          orderBy: Order.DEADLINE,
          status: GoalStatus.ACTIVE,
        })

  const queryKey = userId
    ? Common.UseAdvisorsServiceAdvisorUsersControllerGetGoalsKeyFn({
        userId: +userId,
      })
    : Common.UseGoalsServiceGoalsControllerGetAllKeyFn({
        orderBy: Order.DEADLINE,
        status: GoalStatus.ACTIVE,
      })

  const { data, isFetching, isLoading } = useQuery({
    queryFn,
    queryKey,
  })

  const goals = useMemo<GoalData[]>(() => {
    if (!data) {
      return []
    }

    return data
  }, [data])

  const handleNavigate = () => {
    navigate(ActionPlanRoutePathsEnum.ACTION_PLAN)
  }

  if (isLoading || isFetching) {
    return (
      <ShadowBox flex={1}>
        <Loader isMinimized />
      </ShadowBox>
    )
  }

  return (
    <>
      <ShadowBox flex={1}>
        <Flex direction="column" h="100%">
          <Flex align="center" justify="space-between" mb={18}>
            <Title mb={0} variant="h2">
              Action Plans
            </Title>
            {goals.length > 4 && (
              <UnstyledButton
                className={classes.viewAllButton}
                h={27}
                onClick={isAdvisorViewing ? open : handleNavigate}
              >
                <Text variant="body_2_semibold_grey">View all</Text>
              </UnstyledButton>
            )}
          </Flex>

          {!goals.length ? (
            <Flex h="100%" justify="center">
              <Flex
                align="center"
                className={classes.noGoalsContainer}
                direction="column"
                h="100%"
                justify="center"
                w="100%"
              >
                <Text variant="body_2_black">No goals added</Text>
                {!isAdvisorViewing ? (
                  <Button
                    mt={16}
                    onClick={handleNavigate}
                    variant="primary"
                    w="100%"
                  >
                    Go to action plan
                  </Button>
                ) : (
                  <Text variant="body_2_black">Ask employee to add goal</Text>
                )}
              </Flex>
            </Flex>
          ) : (
            <Flex direction="column" gap={16} mt={14}>
              {goals.slice(0, 4).map(goal => (
                <GoalsWidgetItem key={goal.id} goal={goal} />
              ))}
            </Flex>
          )}
        </Flex>
      </ShadowBox>

      {isAdvisorViewing && (
        <Modal centered onClose={close} opened={opened} withCloseButton={false}>
          <Title variant="h2">All goals</Title>
          <ScrollArea mt={32} pr={8} scrollbars="y">
            <Flex direction="column" gap={16} mah={300}>
              {goals.map(goal => (
                <GoalsWidgetItem key={goal.id} goal={goal} />
              ))}
            </Flex>
          </ScrollArea>

          <Flex justify="center" mt={32}>
            <Button onClick={close} variant="outline">
              Close
            </Button>
          </Flex>
        </Modal>
      )}
    </>
  )
}
